import { ChangeEvent, useCallback, useState } from "react";
import { AxiosResponse } from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  // MenuItem,
  Modal,
  // Select,
  SxProps,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import { Formik } from "formik";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import http from "../../utils/http";
import { DrawerForm } from "../Common/styles/drawer";
import { useParams } from "react-router-dom";
import { Delete } from "@mui/icons-material";
import { consentUploadFile } from "../../utils/upload";

const BoxStyle: SxProps = {
  width: "48px",
  height: "48px",
  borderRadius: "8px",
  border: 1,
  borderColor: "#E3F1F4",
  background: "#ffffff",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

let schema = yup.object().shape({
  consentName: yup.string().required("Name is Required"),
  consentSummary: yup.string().optional(),
  contentType: yup.string().required("Type is Required"),
  fileUrl: yup.string().when("contentType", ([contentType], schema) => {
    return contentType === "file"
      ? schema.required("File is Required")
      : schema.optional();
  }),
});

type Props = {
  showModal: boolean;
  closeModal: () => void;
  data: any;
  refreshPage: () => void;
  previewMode: boolean;
  type: "platform" | "study";
  formType?: "consent" | "sunshine";
};

// const fileOptions = [
//   { label: "File", value: "file" },
//   { label: "Builder", value: "builder" },
// ];

const AddEConsentModal = ({
  showModal,
  closeModal,
  data,
  refreshPage,
  previewMode,
  type,
  formType,
}: Props) => {
  const { id: studyId } = useParams();

  const MAX_FILE_SIZE_5MB = 5 * 1024 * 1024;

  const [submitLoader, setSubmitLoader] = useState(false);
  const [loading, setLoading] = useState(false);

  const isDownloadable = data?.consentId || previewMode;

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      let res: AxiosResponse;
      let body: any = {
        name: values.consentName,
        description: values.consentSummary,
        fileUrl: values?.contentType === "file" ? values.fileUrl : "",
        contentType: values?.contentType,
      };
      // if (data?.status === "active") {
      //   res = await http.post(
      //     `/admin/study/${studyId}/econsent/${data?.consentId}/versions`,
      //     body
      //   );
      // } else
      const prefix =
        type === "platform"
          ? `/platform/admin/econsent`
          : formType === "sunshine"
          ? `/admin/study/${studyId}/sunshine`
          : `/admin/study/${studyId}/econsent`;
      if (data?.consentId) {
        res = await http.patch(`${prefix}/${data?.id}`, body);
      } else {
        res = await http.post(`${prefix}`, body);
      }
      toastMessage("success", res.data.message);
      closeModal();
      refreshPage();
    } catch (err) {
      setSubmitLoader(false);
      errorToastMessage(err as Error);
    }
  };

  const uploadFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>, setFieldValue: any) => {
      try {
        if (e.target.files && e.target.files.length > 0) {
          const files = e.target.files;
          if (files && files.length > 0) {
            const file = files[0];
            if (file.size > MAX_FILE_SIZE_5MB) {
              toastMessage("warning", "File size must be smaller than 5MB!");
              return;
            }
            setLoading(true);
            const obj = await consentUploadFile(file, studyId, type);
            if (obj.postUploadImageUrl) {
              setFieldValue("fileUrl", obj.postUploadImageUrl);
            }
            setFieldValue("fileName", file.name);
            setLoading(false);
          }
        }
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    },
    [studyId, setLoading, MAX_FILE_SIZE_5MB, type]
  );

  const clearFile = (setFieldValue: any) => {
    setFieldValue("fileUrl", "");
    setFieldValue("fileName", "");
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      const prefix =
        type === "platform" ? `/platform/admin/` : `/admin/study/${studyId}/`;
      let res = await http.get(
        `${prefix}econsent/${data?.consentId}/versions/${data?.id}/download`
      );
      const downloadLink = res?.data?.data?.fileUrl;
      window.open(downloadLink, "_blank");
      toastMessage("success", res.data.message);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      errorToastMessage(err as Error);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={
            previewMode
              ? `${formType === "consent" ? "Consent" : "Sunshine Form"}`
              : data?.consentId
              ? `Edit ${formType === "consent" ? "Consent" : "Sunshine Form"}`
              : `Add New ${
                  formType === "consent" ? "Consent" : "Sunshine Form"
                }`
          }
          onCloseClick={closeModal}
        />
        <Formik
          initialValues={{
            consentName: data?.consentName || "",
            consentSummary: data?.consentSummary || "",
            fileName: data?.fileName || "",
            fileUrl: data?.fileUrl || "",
            contentType: data?.contentType || "builder",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({
            handleSubmit,
            getFieldProps,
            setFieldValue,
            values,
            errors,
            touched,
          }) => (
            <form onSubmit={handleSubmit} style={DrawerForm}>
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="consent-name">
                  {formType === "consent" ? "Consent Name" : "Form Name"}{" "}
                  <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <TextField
                  id="consent-name"
                  placeholder={
                    formType === "consent" ? "Consent Name" : "Form Name"
                  }
                  {...getFieldProps("consentName")}
                  error={
                    touched?.consentName && errors?.consentName ? true : false
                  }
                  helperText={
                    touched?.consentName && errors?.consentName
                      ? (errors?.consentName as string)
                      : " "
                  }
                  InputProps={{
                    readOnly: previewMode,
                  }}
                />
              </FormControl>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="consent-summary">
                  {formType === "consent"
                    ? "Consent Summary"
                    : "Form Description"}
                </FormLabel>
                <TextField
                  id="consent-summary"
                  placeholder={
                    formType === "consent"
                      ? "Consent Summary"
                      : "Form Description"
                  }
                  multiline
                  minRows={4}
                  {...getFieldProps("consentSummary")}
                  error={
                    touched?.consentSummary && errors?.consentSummary
                      ? true
                      : false
                  }
                  helperText={
                    touched?.consentSummary && errors?.consentSummary
                      ? (errors?.consentSummary as string)
                      : " "
                  }
                  InputProps={{
                    readOnly: previewMode,
                  }}
                />
              </FormControl>

              {/* <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="file-type">
                  Content Type
                </FormLabel>
                <Select
                  value={values.contentType}
                  onChange={(e) => {
                    setFieldValue("contentType", e.target.value);
                    if (e.target.value === "builder") {
                      setFieldValue("fileUrl", "");
                      setFieldValue("fileName", "");
                    }
                  }}
                  fullWidth
                  id="file-type"
                  inputProps={{
                    readOnly: previewMode,
                  }}
                >
                  {fileOptions.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={
                    touched?.contentType && errors?.contentType ? true : false
                  }
                >
                  {touched?.contentType && errors?.contentType
                    ? (errors?.contentType as string)
                    : " "}
                </FormHelperText>
              </FormControl> */}

              {values?.contentType === "file" && (
                <FormControl sx={{ ...InputWrapper, mb: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="file-upload">
                    File Upload <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      id="file-upload"
                      placeholder="Upload file .PDF format"
                      value={values.fileName}
                      inputProps={{
                        readOnly: true,
                        style: {
                          fontSize: "16px",
                          fontWeight: 500,
                          color: "#327091",
                        },
                      }}
                      sx={{
                        width: "50%",
                      }}
                    />
                    {isDownloadable && values.fileUrl && (
                      <>
                        {!loading ? (
                          <Button
                            variant="contained"
                            onClick={handleDownload}
                            sx={{ height: "48px" }}
                          >
                            Download
                          </Button>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              height: "48px",
                            }}
                          >
                            <CircularProgress size={25} />
                          </Box>
                        )}
                      </>
                    )}

                    {values.fileUrl && !previewMode ? (
                      <Box sx={BoxStyle}>
                        <IconButton onClick={() => clearFile(setFieldValue)}>
                          <Delete color="error" />
                        </IconButton>
                      </Box>
                    ) : (
                      !previewMode && (
                        <>
                          {loading ? (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <CircularProgress size={25} />
                            </Box>
                          ) : (
                            <Button
                              variant="outlined"
                              component="label"
                              sx={{ height: "40px" }}
                            >
                              Upload File
                              <input
                                hidden
                                accept="application/pdf"
                                type="file"
                                onChange={(e) => uploadFile(e, setFieldValue)}
                              />
                            </Button>
                          )}
                        </>
                      )
                    )}
                  </Box>
                  <FormHelperText
                    error={touched?.fileUrl && errors?.fileUrl ? true : false}
                  >
                    {touched?.fileUrl && errors?.fileUrl
                      ? (errors?.fileUrl as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
              )}

              {!previewMode && (
                <Box sx={{ mt: "auto" }}>
                  <Divider />
                  <Box sx={ModalDrawerButtonStyles}>
                    {!submitLoader ? (
                      <>
                        <Button onClick={closeModal} variant="outlined">
                          Cancel
                        </Button>
                        <Button type="submit" variant="contained">
                          Submit
                        </Button>
                      </>
                    ) : (
                      <CircularProgress size={25} />
                    )}
                  </Box>
                </Box>
              )}
            </form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddEConsentModal;
